import type { IAirplaneInfo } from '@/pages/PlanDetail/hooks/useAirplane.js';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo } from 'react';

function getGMT(endTime?: string) {
  if (!endTime) return 8;
  if (endTime.includes('GMT+')) {
    const [first, second] = endTime.split('GMT+')[1].split('');
    let result = Number(first);
    if (!Number.isNaN(Number(second))) {
      result += Number(second);
    }
    return result;
  }
  return 8;
}

function getTimeDifference(startTime: Dayjs, endTime: Dayjs) {
  // 计算两个时间点之间的差异（以毫秒为单位）
  const diffInMilliseconds = endTime.diff(startTime);

  // 将毫秒转换为分钟
  const diffInMinutes = Math.floor(diffInMilliseconds / (60 * 1000));

  // 计算小时数
  const hours = Math.floor(diffInMinutes / 60);

  // 计算剩余的分钟数
  const minutes = diffInMinutes % 60;

  // 返回格式化的字符串 'XhYm'
  return `${hours}h${minutes ? `${minutes}m` : ''}`;
}

function parseTimeString(str?: string) {
  if (!str) return undefined;

  // 定义正则表达式
  const regex = /(\d{2}:\d{2})(?: GMT([+-]\d+))?/;
  const match = str.match(regex);

  if (match) {
    const time = match[1];
    let gmt = 0;

    if (match[2]) {
      gmt = Number.parseInt(match[2], 10);
    }

    return { time, gmt };
  }

  throw new Error('Invalid time string');
}

const useAirplaneTime = ({
  startTime,
  endTime,
  date,
}: Pick<IAirplaneInfo, 'startTime' | 'endTime'> & { date: string }) => {
  const result = useMemo(() => {
    const startTimeInfo = parseTimeString(startTime);
    const endTimeInfo = parseTimeString(endTime);

    const startTimeInst = dayjs(`${date} ${startTimeInfo?.time}`).utcOffset(
      startTimeInfo?.gmt || 8
    );
    let endTimeInst = dayjs(`${date} ${endTimeInfo?.time}`).utcOffset(
      endTimeInfo?.gmt || 8
    );
    if (startTimeInst > endTimeInst) {
      endTimeInst = endTimeInst.add(1, 'day');
    }

    const dayDiff = endTimeInst
      .startOf('day')
      .diff(startTimeInst.startOf('day'), 'day');

    const duration = getTimeDifference(startTimeInst, endTimeInst);

    return {
      dayDiff,
      startTimeText: startTimeInst
        .utcOffset(startTimeInfo?.gmt || 8)
        .format('HH:mm'),
      endTimeText: endTimeInst.utcOffset(endTimeInfo?.gmt || 8).format('HH:mm'),
      duration,
    };
  }, []);

  return result;
};

export default useAirplaneTime;
